import moment from 'moment-timezone';

const timezoneValidationCache = new Map<string, boolean>();

/**
 * Parses GMT offset timezone strings like "Etc/GMT-3.5" to extract the offset
 * @param timezone - The timezone string to parse
 * @returns The offset in hours, or null if not a GMT offset timezone
 */
function parseGMTOffset(timezone: string): number | null {
  const gmtOffsetMatch = timezone.match(/^Etc\/GMT([+-]?)(\d+(?:\.\d+)?)$/);
  if (!gmtOffsetMatch) {
    return null;
  }

  const sign = gmtOffsetMatch[1] === '+' ? -1 : 1; // Note: Etc/GMT signs are inverted
  const offset = parseFloat(gmtOffsetMatch[2]);
  return sign * offset;
}

/**
 * Finds a timezone with the matching offset from moment's timezone list
 * @param targetOffset - The target offset in hours
 * @returns A valid timezone name, or null if none found
 */
function findTimezoneByOffset(targetOffset: number): string | null {
  const allTimezones = moment.tz.names();
  const now = moment();

  for (const tz of allTimezones) {
    try {
      const tzMoment = now.clone().tz(tz);
      const currentOffset = tzMoment.utcOffset() / 60; // Convert minutes to hours

      if (Math.abs(currentOffset - targetOffset) < 0.01) {
        // Allow small floating point differences
        return tz;
      }
    } catch {
      // Skip invalid timezones
      continue;
    }
  }

  return null;
}

/**
 * Normalizes a timezone identifier to ensure it's valid for moment-timezone
 * @param timezone - The timezone identifier to normalize
 * @returns A valid timezone identifier, or 'UTC' as fallback
 */
export function normalizeTimezone(timezone: string | undefined): string {
  if (!timezone) {
    return 'UTC';
  }

  if (isValidTimezone(timezone)) {
    return timezone;
  }

  const gmtOffset = parseGMTOffset(timezone);
  if (gmtOffset !== null) {
    const matchingTimezone = findTimezoneByOffset(gmtOffset);
    if (matchingTimezone) {
      return matchingTimezone;
    }
  }

  console.warn(
    `Could not normalize timezone "${timezone}", falling back to UTC`,
  );
  return 'UTC';
}

/**
 * Checks if a timezone identifier is valid for moment-timezone
 * @param timezone - The timezone identifier to check
 * @returns true if the timezone is valid, false otherwise
 */
export function isValidTimezone(timezone: string): boolean {
  // Check cache first
  if (timezoneValidationCache.has(timezone)) {
    return timezoneValidationCache.get(timezone)!;
  }

  let isValid = false;
  try {
    // Check if timezone exists in moment's timezone names
    const allTimezones = moment.tz.names();
    if (allTimezones.includes(timezone)) {
      // Double-check by trying to create a moment with the timezone
      moment().tz(timezone);
      isValid = true;
    }
  } catch {
    isValid = false;
  }

  // Cache the result
  timezoneValidationCache.set(timezone, isValid);
  return isValid;
}

/**
 * Gets all available timezone names from moment-timezone
 * @returns Array of all valid timezone names
 */
export function getAllTimezones(): string[] {
  return moment.tz.names();
}

/**
 * Gets timezone information including current offset
 * @param timezone - The timezone to get info for
 * @returns Object with timezone info, or null if invalid
 */
export function getTimezoneInfo(timezone: string): {
  name: string;
  offset: number;
  offsetString: string;
  abbreviation: string;
} | null {
  if (!isValidTimezone(timezone)) {
    return null;
  }

  try {
    const now = moment().tz(timezone);
    const offset = now.utcOffset() / 60; // Convert minutes to hours
    const offsetString = now.format('Z');
    const abbreviation = now.format('z');

    return {
      name: timezone,
      offset,
      offsetString,
      abbreviation,
    };
  } catch {
    return null;
  }
}
